import React, { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ThemeProvider } from 'react-jss';
import { ReactNotifications } from 'react-notifications-component';
import { useFullscreen } from 'react-use';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';
// import { Route, Routes, useNavigate, useLocation } from 'react-router-dom';
import { ToastProvider } from 'react-toast-notifications';
import { TourProvider } from '@reactour/tour';
import { GoogleOAuthProvider } from '@react-oauth/google';
import ThemeContext from '../contexts/themeContext';
import Aside from '../layout/Aside/Aside';
import Wrapper from '../layout/Wrapper/Wrapper';
import Portal from '../layout/Portal/Portal';
import { demoPages, layoutMenu } from '../menu';
// import { demoPages, layoutMenu } from '../menu';
import { Toast, ToastContainer } from '../components/bootstrap/Toasts';
import useDarkMode from '../hooks/useDarkMode';
import COLORS from '../common/data/enumColors';
import { getOS } from '../helpers/helpers';
import steps, { styles } from '../steps';
// import { userAuth } from '../contexts/userContext';
import Loader from '../pages/common/vhelper/Loader';
import { Ralert } from '../pages/common/vhelper/Normarodallalert';
import InactivityTimer from '../pages/common/vhelper/inactivityTimer';
import { userAuth } from '../contexts/userContext';
import ErrorBoundary from '../pages/presentation/demo-pages/helper/ErrorBoundaries';
import UiModal from '../modal/Uimodal.js';
import { LanguageSwitchProvider } from '../contexts/LanguageSwitchContext';
import { LoginSettingsProvider } from '../contexts/loginSettingsContext';
import { CartProvider } from '../contexts/CartContext';
import GlobalAlertListener from '../components/GlobalAlertListener';
import { useTranslation } from 'react-i18next';
import {
	isGoogleTranslateActive,
} from '../helpers/languageSwitch';


const MaintenanceOverlay = ({ onClose, isOpen }) => {
	return (
		<UiModal isOpen={isOpen}>
			<div
				style={{
					width: '100%',
					height: '100%',
					backgroundColor: 'rgba(0, 0, 0, 0.9)',
					// zIndex: 999,
					color: 'white',
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
					flexDirection: 'column',
					textAlign: 'center',
					padding: '20px',
				}}
				onClick={onClose}>
				<h1 style={{ fontSize: '2.5rem', marginBottom: '20px' }}>
					🚀 We’re Under Maintenance!
				</h1>
				<p style={{ fontSize: '1.2rem', maxWidth: '750px', lineHeight: '1.6' }}>
					We’re currently making improvements to enhance your experience. Our site is
					temporarily unavailable due to scheduled maintenance. Please check back soon to
					explore our upgraded platform with exciting new features and improvements.
				</p>
				<p style={{ marginTop: '1px', opacity: 0.8 }}>
					We appreciate your understanding and patience.!
				</p>
			</div>
		</UiModal>
	);
};

const App = () => {
	getOS();

	/**
	 * Dark Mode
	 */
	const { themeStatus, darkModeStatus } = useDarkMode();
	const [showOverlay, setShowOverlay] = useState(false);

	const [loaderv] = useState(false);
	const [servicepageroute, setServicepageroute] = useState(''); // n
	// Error alert for customer_active API
	const [showCustomerActiveError, setShowCustomerActiveError] = useState(false);
	const [customerActiveErrorMsg, setCustomerActiveErrorMsg] = useState('');

	const Location = useLocation();
	const navigate = useNavigate();
	const { token, setToken } = useContext(userAuth);

	const { fullScreenStatus, setFullScreenStatus } = useContext(ThemeContext);
	const ref = useRef(null);
	useFullscreen(ref, fullScreenStatus, {
		onClose: () => setFullScreenStatus(false),
	});
	const { t } = useTranslation();

	const theme = {
		theme: themeStatus,
		primary: COLORS.PRIMARY.code,
		secondary: COLORS.SECONDARY.code,
		success: COLORS.SUCCESS.code,
		info: COLORS.INFO.code,
		warning: COLORS.WARNING.code,
		danger: COLORS.DANGER.code,
		dark: COLORS.DARK.code,
		light: COLORS.LIGHT.code,
	};

	useEffect(() => {
		if (darkModeStatus) {
			document.documentElement.setAttribute('theme', 'dark');
		}
		return () => {
			document.documentElement.removeAttribute('theme');
		};
	}, [darkModeStatus]);

	useEffect(() => {
		const originalPushState = window.history.pushState.bind(window.history);
		const originalReplaceState = window.history.replaceState.bind(window.history);

		const redirectWithFullReload = (url, replace = false) => {
			if (!url || !isGoogleTranslateActive()) {
				return false;
			}

			const nextUrl = new URL(url, window.location.href);
			if (nextUrl.origin !== window.location.origin) {
				return false;
			}

			if (nextUrl.href === window.location.href) {
				return false;
			}

			if (replace) {
				window.location.replace(nextUrl.href);
			} else {
				window.location.assign(nextUrl.href);
			}
			return true;
		};

		window.history.pushState = function pushState(state, unused, url) {
			if (redirectWithFullReload(url, false)) {
				return;
			}
			return originalPushState(state, unused, url);
		};

		window.history.replaceState = function replaceState(state, unused, url) {
			if (redirectWithFullReload(url, true)) {
				return;
			}
			return originalReplaceState(state, unused, url);
		};

		return () => {
			window.history.pushState = originalPushState;
			window.history.replaceState = originalReplaceState;
		};
	}, []);

	useEffect(() => {
		const pathname = Location.pathname;
		const publicPages = [
			demoPages.login.path,
			demoPages.signUp.path,
			demoPages.privacy.path,
			demoPages.terms.path,
			demoPages.forgot.path,
			demoPages.mobileotp.path,
			demoPages.changepw.path,
		].map((path) => (path.startsWith('/') ? path : `/${path}`));
		const agentPages = ['/dashboard', '/my-business', '/transaction-history', '/payments', '/settings'];
		const isAgentLoggedIn = token?.userrole === 'ref_agent' && !!token?.session_key;

		if (pathname === '/') {
			navigate(isAgentLoggedIn ? '/dashboard' : '/login', { replace: true });
		} else if (pathname === demoPages.login.path && isAgentLoggedIn) {
			navigate('/dashboard', { replace: true });
		} else if (publicPages.includes(pathname)) {
			// Public pages do not need agent auth redirect handling.
		} else if (!isAgentLoggedIn) {
			remove_login();
		} else if (!agentPages.includes(pathname)) {
			navigate('/dashboard', { replace: true });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [Location.pathname, token?.session_key, token?.userrole]);

	const remove_login = () => {
		setToken({});
		localStorage.removeItem('user-auth');
		navigate('/login', { replace: true });
	};

	const call_default = () => {
		let servicepage = Location.pathname.split('/servicewl');

		if (servicepage.length === 2) {
			setServicepageroute(servicepage[1]);
		}
	};

	/*
	removed 30/10/2024 by pramudika
	useEffect(() => {
		 
		
		let servicepage = Location.pathname.split('/servicewl');
		

		if (servicepage.length === 2) {
			setServicepageroute(servicepage[1]);
		}
	},[Location.pathname]);*/

	//
	// useEffect(() => {
	// 	let authcheck;
	// 	let authcheck2;

	// /servicewl
	// let servicepage=location.pathname.split("/servicewl");
	//

	// let servicepageroute1 = ''

	// if(servicepage.length===2){
	// 	setServicepageroute(servicepage[1])
	// 	servicepageroute1=servicepage[1]
	// }

	// 	if (token.email) {
	// 		setLoaderv(false);
	// 		authcheck = setInterval(() => {
	// 			(async () => {
	// 				const web_link = process.env.REACT_APP_WEB_LINK;
	// 				const data = { action: 'checkcuslog', email: token.email };
	// 				const res = await fetch(web_link, {
	// 					method: 'post',
	// 					headers: {
	// 						'Content-Type': 'application/json',
	// 						Accept: 'application/json',
	// 						'sdk-authorization': `${token.session_key}`,
	// 					},
	// 					body: JSON.stringify(data),
	// 				});
	// 				const responce = await res.json();
	// 				if (responce.type == 'error') {
	// 					setToken({});
	// 					localStorage.removeItem('user-auth');
	// 					 navigate('/login');
	// 				}
	// 			})();
	// 		}, 1000 * 60);
	// 	} else {
	// 		setLoaderv(true);
	// 		authcheck2 = setTimeout(() => {
	// 			//
	// 			(async () => {
	// 				const web_link = process.env.REACT_APP_WEB_LINK;
	// 				//
	// 				const data = { action: 'checkcuslog', email: token.email };
	// 				//
	// 				const res = await fetch(web_link, {
	// 					method: 'post',
	// 					headers: {
	// 						'Content-Type': 'application/json',
	// 						Accept: 'application/json',
	// 						'sdk-authorization': `${token.session_key}`,
	// 					},
	// 					body: JSON.stringify(data),
	// 				}).catch(() => {
	// 					setToken({});
	// 					setLoaderv(false);
	// 					localStorage.removeItem('user-auth');
	// 					if(location.pathname === '/landing/privacy' || location.pathname === '/landing/forgot' || location.pathname === '/landing/terms'|| location.pathname === '/landing/changepw'|| location.pathname === '/servicewl'+servicepageroute1 || location.pathname === '/'){
	//
	// 					}else{
	// 						 navigate('/login');
	//
	// 					}

	// 				});

	// 				const responce = await res.json();
	//
	// 				if (responce.type == 'error') {
	// 					setToken({});
	// 					setLoaderv(false);
	// 					localStorage.removeItem('user-auth');
	// 					if(location.pathname === '/landing/privacy' || location.pathname === '/landing/forgot' || location.pathname === '/landing/terms'|| location.pathname === '/landing/changepw' || location.pathname === '/servicewl'+servicepageroute1 || location.pathname === '/'){
	//
	// 					}else{
	// 						 navigate('/login');
	//
	// 					}
	// 				} else {
	// 					setLoaderv(false);
	// 				}
	// 			})();
	// 		}, 300);
	// 	}

	// 	return () => {
	// 		clearInterval(authcheck);
	// 		clearTimeout(authcheck2);
	// 	};
	// }, [token.email,location.pathname]);

	/**
	 * Modern Design
	 */
	useLayoutEffect(() => {
		if (process.env.REACT_APP_MODERN_DESGIN === 'true') {
			document.body.classList.add('modern-design');
		} else {
			document.body.classList.remove('modern-design');
		}
	});

	const withOutAsidePages = [
		demoPages.login.path,
		demoPages.signUp.path,
		demoPages.privacy.path,
		demoPages.terms.path,
		demoPages.forgot.path,
		demoPages.mobileotp.path,
		demoPages.changepw.path,
		demoPages.HHome.path,
		`${layoutMenu.view_servwl.path}`,
		`${layoutMenu.view_servwl.path}/*`,
		`${layoutMenu.view_productvwl.path}`,
		`${layoutMenu.view_productvwl.path}/*`,
		'payment',
		'completion',
	];

	const shouldHideAside = withOutAsidePages.some((path) => {
		const normalizedPath = path.startsWith('/') ? path : `/${path}`;
		return Boolean(
			matchPath(
				{
					path: normalizedPath,
					end: !normalizedPath.endsWith('/*'),
				},
				Location.pathname,
			),
		);
	});

	// const withOutAsidePages = [demoPages.login.path, demoPages.signUp.path,demoPages.privacy.path,demoPages.terms.path,demoPages.forgot.path,demoPages.changepw.path,demoPages.HHome.path,layoutMenu.cbookingPages.subMenu.view_servwl.path+servicepageroute,'payment','completion'];
	if (loaderv) {
		return <Loader />;
	}
	return (
		<ErrorBoundary>
			<GoogleOAuthProvider clientId='860432327460-k124g6nolj6rbc8n34s6t8haaif8rnu7.apps.googleusercontent.com'>
				<LanguageSwitchProvider>
					<ThemeProvider theme={theme}>
						<LoginSettingsProvider>
							<CartProvider>
								<InactivityTimer />
								<ToastProvider components={{ ToastContainer, Toast }}>
									<TourProvider
										steps={steps}
										styles={styles}
										showNavigation={false}
										showBadge={false}>
										<MaintenanceOverlay isOpen={showOverlay} />
										<Ralert
											msgloadalert={
												customerActiveErrorMsg || 'Account login failed.'
											}
											msgtypeloadalert={t('Error')}
											loadalert={showCustomerActiveError}
											closemodel={() => setShowCustomerActiveError(false)}
										/>
										<div
											ref={ref}
											className='app'
											style={{
												backgroundColor: darkModeStatus
													? 'var(--bs-body-bg)'
													: '#ececec',
												zIndex: fullScreenStatus && 1,
												overflow: fullScreenStatus && 'scroll',
											}}>
											<GlobalAlertListener />
											{!shouldHideAside && <Aside />}
											<Wrapper />
										</div>
										<Portal id='portal-notification'>
											<ReactNotifications />
										</Portal>
									</TourProvider>
								</ToastProvider>
							</CartProvider>
						</LoginSettingsProvider>
					</ThemeProvider>
				</LanguageSwitchProvider>
			</GoogleOAuthProvider>
		</ErrorBoundary>
	);
};

export default App;
